import {ButtonHTMLAttributes, FC} from 'react';
import {FieldError, useFormContext} from 'react-hook-form';
import clsx from 'clsx';
import Button from 'components/Button';
import CheckboxRadioGroup from 'components/Form/CheckboxRadioGroup';
import {RequiredRules} from 'components/Form/types';
import {getRegisterRules} from 'components/Form/utils';
import {Option} from 'types';

export type BaseRadioButtonProps<T = HTMLButtonElement> = RequiredRules<
    ButtonHTMLAttributes<T>
> & {
    error?: FieldError;
    isHorizontal?: boolean;
    name: string;
    options: Option[];
    type?: never;
};

const BaseRadioButton: FC<BaseRadioButtonProps> = ({
    children,
    className,
    disabled,
    error,
    id,
    isHorizontal,
    name,
    options,
    required,
    rules,
    ...rest
}) => {
    const {setValue, watch} = useFormContext();
    const value = watch(name);

    const registerRules = getRegisterRules({disabled, required, rules});

    return (
        <CheckboxRadioGroup
            className={className}
            isButton={true}
            isHorizontal={isHorizontal}
        >
            {options.map((option) => (
                <Button
                    key={option.value}
                    aria-checked={value === option.value}
                    aria-label={`${name}-${option.value.toString()}`}
                    className={clsx(isHorizontal && 'flex-1')}
                    kind={value === option.value ? 'primary' : 'secondary'}
                    onClick={() => setValue(name, option.value)}
                    role="radio"
                    {...rest}
                    {...registerRules}
                    disabled={
                        disabled || option.disabled || registerRules?.disabled
                    }
                >
                    {option.label}
                </Button>
            ))}
            {children}
        </CheckboxRadioGroup>
    );
};

export default BaseRadioButton;
